import { useEffect, useState } from 'react';
import type { StoryboardPage } from '../services/GameApiService';

interface GraphicNovelIntroProps {
  pages: StoryboardPage[];
  /** Called when the player finishes (or skips) the intro. */
  onComplete: () => void;
}

/**
 * Spoiler-free graphic-novel intro shown once, before the first conversation of
 * episode 1. Four illustrated pages, each with a short caption, that drop the
 * player into the world before they speak to anyone.
 *
 * Layout:
 *   - Mobile: a vertical 50/50 split — image occupies the top half of the
 *     screen, caption + controls occupy the bottom half on a solid black
 *     panel. Neither overlaps the other, so both stay fully visible and
 *     readable even on tall, narrow phones. The caption area is scrollable
 *     (flex-1 with min-h-0); progress dots and the "Continuar" button stay
 *     pinned below it.
 *   - Desktop (md+): a two-column split — image on the left half, caption +
 *     controls on the right half. The caption area is scrollable (flex-1 with
 *     min-h-0 so overflow works inside flexbox).
 *   - The player advances one page at a time via the "Continuar" / "Começar"
 *     button.
 */
export default function GraphicNovelIntro({ pages, onComplete }: GraphicNovelIntroProps) {
  const [index, setIndex] = useState(0);
  const isLast = index >= pages.length - 1;

  // Preload every page image up front so advancing is instant.
  useEffect(() => {
    pages.forEach((p) => {
      const img = new Image();
      img.src = p.imageUrl;
    });
  }, [pages]);

  if (!pages || pages.length === 0) {
    // Nothing to show — get out of the way immediately.
    onComplete();
    return null;
  }

  const page = pages[index];

  const advance = () => {
    if (isLast) {
      onComplete();
    } else {
      setIndex((i) => Math.min(i + 1, pages.length - 1));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black select-none flex flex-col md:flex-row">
      {/* Illustration.
          Mobile: top half of the screen (h-1/2), in normal flow so it never
          sits behind the caption. object-contain keeps the whole image
          readable (object-cover would crop heads/feet on landscape images);
          the black background acts as letterbox for any aspect-ratio mismatch.
          Desktop: left half, full height. */}
      <div className="relative h-1/2 md:h-full md:w-1/2 md:flex-shrink-0 flex-shrink-0">
        <img
          key={page.imageUrl}
          src={page.imageUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-contain bg-black animate-[fadeIn_400ms_ease-out]"
          draggable={false}
        />
      </div>

      {/* Skip */}
      <button
        onClick={onComplete}
        className="absolute top-4 right-4 z-20 text-white/70 hover:text-white text-sm font-medium px-3 py-1.5 rounded-full bg-black/30 backdrop-blur-sm transition-colors"
      >
        Saltar
      </button>

      {/* Caption + controls.
          Mobile: bottom half of the screen (h-1/2) on a solid black panel —
          no overlap with the image, so both stay fully visible and readable.
          Text scrolls in the flex-1 area; dots + button stay pinned below.
          Desktop: right half, full height — text scrolls in the flex-1 area. */}
      <div className="relative z-10 h-1/2 md:h-full md:w-1/2 flex flex-col">
        {/* Scrollable text area. m-auto on the <p> centers short text while
            keeping long text scrollable from the top (justify-center would
            clip the top of overflowed content). */}
        <div
          key={`text-${index}`}
          className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain
                     px-6 pt-6 pb-4 md:px-12 md:pt-12
                     flex flex-col items-center
                     animate-[fadeIn_500ms_ease-out]
                     [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.3)_transparent]
                     [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/30"
        >
          <p className="m-auto text-white text-center text-base md:text-2xl leading-relaxed max-w-2xl
                        whitespace-pre-line">
            {page.text}
          </p>
        </div>

        {/* Controls — always visible below the scroll area */}
        <div className="flex flex-col items-center gap-3 md:gap-4 px-6 pb-6 pt-2 md:px-12 md:pb-12 flex-shrink-0">
          {/* Progress dots */}
          <div className="flex items-center gap-2">
            {pages.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Página ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === index ? 'w-8 bg-primary' : 'w-2 bg-white/40 hover:bg-white/70'
                }`}
              />
            ))}
          </div>

          <button
            onClick={advance}
            className="px-10 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
          >
            {isLast ? 'Começar' : 'Continuar'}
          </button>
        </div>
      </div>
    </div>
  );
}